import { useCallback, useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { fetchStorageSpaces, updateStorageSpaceQuota, type StorageSpace } from '@/api/console';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const STORAGE_SPACES_QUERY_KEY = 'admin-storage-spaces';

/**
 * ECR-85: the "Kho lưu trữ" view model — the paginated, searchable space list
 * and the one write it offers (a space's quota override).
 *
 * The list is platform-wide (every organisation), so its key carries no acting
 * org: the proxy answers the same rows whichever org the console is acting in.
 * The token IS in the key, like every other console query, so a re-login never
 * reads a previous session's cache.
 */
export function useStorageSpaces(enabled: boolean) {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [searchInput]);

    const spacesQuery = useQuery({
        queryKey: [STORAGE_SPACES_QUERY_KEY, accessToken, page, pageSize, search],
        queryFn: () =>
            fetchStorageSpaces(config.apiBase, accessToken as string, { page, pageSize, search }),
        enabled: enabled && Boolean(accessToken),
        placeholderData: keepPreviousData,
        retry: false,
    });

    const quotaMutation = useMutation({
        mutationFn: (args: { spaceId: string; quotaOverrideBytes: number | null }) =>
            updateStorageSpaceQuota(
                config.apiBase,
                accessToken as string,
                args.spaceId,
                args.quotaOverrideBytes
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [STORAGE_SPACES_QUERY_KEY] });
        },
    });

    const handlePageChange = useCallback((nextPage: number, nextPageSize: number) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    }, []);

    const spaces: StorageSpace[] = spacesQuery.data?.results ?? [];

    return {
        spaces,
        total: spacesQuery.data?.count ?? 0,
        page,
        pageSize,
        searchInput,
        setSearchInput,
        handlePageChange,
        spacesQuery,
        quotaMutation,
    };
}

export type StorageSpacesVM = ReturnType<typeof useStorageSpaces>;
